import React, { useState, Component } from 'react';
/**
 * GovernanceRulesTab — Main governance rules browser.
 *
 * Three views: validation rules, rulebook files, and policy thresholds.
 * Canonical location: portals/admin/content/governance/ (DDD — UI in portal)
 */
import { SearchIcon, ShieldCheckIcon, FilterIcon, FolderIcon, CodeIcon, SlidersIcon, SparklesIcon, TagIcon } from 'lucide-react';
import type { Severity, Stage, ViewId } from './governanceRulesData';
import { RULES, PIPELINE_FILES, STAGE_LABELS, STAGE_COLORS, ALL_STAGES } from './governanceRulesData';
import { RuleCard, ThresholdSection } from './GovernanceRulesCards';
import { LiveRulebookCard } from './LiveRulebookCard';
import { RulebookHintsPanel } from './RulebookHintsPanel';
import { listModuleRulebooks, countRoadmapEntries } from '../../../../governance';
import { Input } from '../../../../components/ui/Input';
import { Select } from '../../../../components/ui/Select';
// ─── Color mapping (shortCode → RULEBOOK_COLOR_MAP key) ───────
// Keeps the rulebook cards visually distinct per module. Falls
// back to 'blue' for unknown codes.
const SHORT_CODE_COLOR: Record<string, string> = {
  GET: 'blue',
  REK: 'indigo',
  MET: 'emerald',
  TIJD: 'amber',
  GELD: 'yellow',
  SPR: 'pink',
  SWW: 'rose',
  TAA: 'purple',
  STU: 'cyan',
  TOPO: 'orange'
};
function moduleColorForShortCode(code: string): string {
  return SHORT_CODE_COLOR[code] ?? 'blue';
}
// ─── View Definitions ─────────────────────────────────────────
const VIEWS: {
  id: ViewId;
  label: string;
  icon: React.ReactNode;
}[] = [{
  id: 'regels',
  label: 'Validatieregels',
  icon: <ShieldCheckIcon className="h-4 w-4" data-id="element-4403" data-mp="3cw1hdi9bsphw" />
}, {
  id: 'regelboeken',
  label: 'Regelboeken',
  icon: <FolderIcon className="h-4 w-4" data-id="element-4404" data-mp="37y9g3xmsjr11" />
}, {
  id: 'family-hints',
  label: 'Family-hints',
  icon: <TagIcon className="h-4 w-4" data-id="element-4405" data-mp="9alyzdl1jz97" />
}, {
  id: 'drempelwaarden',
  label: 'Drempelwaarden',
  icon: <SlidersIcon className="h-4 w-4" data-id="element-4406" data-mp="4fpydp1qo3kh" />
}];
// ─── Severity Counts (static, 5-level) ────────────────────────
// Phase 1: rules can be tagged CRITICAL / REVIEW alongside legacy
// BLOCKER / WARNING / INFO. All five buckets are surfaced so admins
// can filter on semantische ernst (student-facing risk, uncertainty, etc.).
const SEVERITY_TILES: Array<{
  key: Severity;
  label: string;
  textClass: string;
  bgClass: string;
  borderClass: string;
}> = [{
  key: 'CRITICAL',
  label: 'Kritiek',
  textClass: 'text-red-700',
  bgClass: 'bg-red-50',
  borderClass: 'border-red-100'
}, {
  key: 'BLOCKER',
  label: 'Blokkerend',
  textClass: 'text-orange-700',
  bgClass: 'bg-orange-50',
  borderClass: 'border-orange-100'
}, {
  key: 'REVIEW',
  label: 'Review',
  textClass: 'text-yellow-700',
  bgClass: 'bg-yellow-50',
  borderClass: 'border-yellow-100'
}, {
  key: 'WARNING',
  label: 'Coach',
  textClass: 'text-blue-700',
  bgClass: 'bg-blue-50',
  borderClass: 'border-blue-100'
}, {
  key: 'INFO',
  label: 'Info',
  textClass: 'text-slate-700',
  bgClass: 'bg-slate-50',
  borderClass: 'border-slate-100'
}];
const SEVERITY_COUNTS: Record<Severity, number> = {
  CRITICAL: RULES.filter(r => r.severity === 'CRITICAL').length,
  BLOCKER: RULES.filter(r => r.severity === 'BLOCKER').length,
  REVIEW: RULES.filter(r => r.severity === 'REVIEW').length,
  WARNING: RULES.filter(r => r.severity === 'WARNING').length,
  INFO: RULES.filter(r => r.severity === 'INFO').length
};
// ─── Component ────────────────────────────────────────────────
export function GovernanceRulesTab() {
  const [view, setView] = useState<ViewId>('regels');
  const [search, setSearch] = useState('');
  const [selectedStage, setSelectedStage] = useState<Stage | 'all'>('all');
  const [selectedSeverity, setSelectedSeverity] = useState<Severity | 'all'>('all');
  const filtered = RULES.filter(rule => {
    const matchesSearch = search === '' || rule.label.toLowerCase().includes(search.toLowerCase()) || rule.description.toLowerCase().includes(search.toLowerCase()) || rule.id.toLowerCase().includes(search.toLowerCase());
    const matchesStage = selectedStage === 'all' || rule.stage === selectedStage;
    const matchesSeverity = selectedSeverity === 'all' || rule.severity === selectedSeverity;
    return matchesSearch && matchesStage && matchesSeverity;
  });
  return <div className="space-y-5" data-id="element-4407" data-mp="1rltyoqaea7nd">
      {/* View Switcher */}
      <div className="flex gap-1 rounded-xl border border-gray-100 bg-white p-1" data-id="element-4408" data-mp="1vl2us1swqhm3">
        {VIEWS.map(v => <button key={v.id} onClick={() => setView(v.id)} className={`flex flex-1 items-center justify-center gap-2 rounded-lg px-3 py-2 text-sm font-medium transition-all ${view === v.id ? 'bg-blue-600 text-white shadow-sm' : 'text-gray-500 hover:bg-gray-50 hover:text-gray-700'}`} data-id="element-4409" data-mp="20jw89u8728m9">
            {v.icon}
            <span className="hidden sm:inline" data-id="element-4410" data-mp="15btztvnrrlyd">{v.label}</span>
          </button>)}
      </div>

      {/* ── View: Validatieregels ── */}
      {view === 'regels' && <>
          {/* Summary — 5-bucket severity tiles (Phase 1).
                        Click to filter the rule list by that severity. */}
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3" data-id="element-4411" data-mp="rclyaw44w1r0">
            {SEVERITY_TILES.map(tile => {
          const isActive = selectedSeverity === tile.key;
          const count = SEVERITY_COUNTS[tile.key];
          return <button key={tile.key} type="button" onClick={() => setSelectedSeverity(isActive ? 'all' : tile.key)} aria-pressed={isActive} className={`rounded-lg border p-3 text-center transition-all ${tile.bgClass} ${tile.borderClass} ${isActive ? 'ring-2 ring-offset-1 ring-gray-400 shadow-sm' : 'hover:shadow-sm'}`} data-id="element-4412" data-mp="hk6fjwl0j952">
                  <p className={`text-2xl font-bold ${tile.textClass}`} data-id="element-4413" data-mp="3l4dz7fto6uh4">
                    {count}
                  </p>
                  <p className={`text-xs font-medium ${tile.textClass}`} data-id="element-4414" data-mp="3p83ns5achc25">
                    {tile.label}
                  </p>
                </button>;
        })}
          </div>

          {/* Filters */}
          <div className="flex flex-col gap-3 sm:flex-row" data-id="element-4415" data-mp="3g7x9ih6to81x">
            <div className="flex-1" data-id="element-4416" data-mp="7qsscbece4xz">
              <Input type="text" placeholder="Zoek regels op naam, beschrijving of ID..." value={search} onChange={e => setSearch(e.target.value)} icon={<SearchIcon className="w-4 h-4" data-id="element-4418" data-mp="2z0ynrzw3j3m" />} variant="admin" size="sm" data-id="element-4417" data-mp="cpdtcd1f4snh" />
            </div>
            <Select value={selectedSeverity} onChange={e => setSelectedSeverity(e.target.value as Severity | 'all')} options={[{
          value: 'all',
          label: 'Alle ernst'
        }, {
          value: 'CRITICAL',
          label: '🔴 Kritiek'
        }, {
          value: 'BLOCKER',
          label: '🟠 Blokkerend'
        }, {
          value: 'REVIEW',
          label: '🟡 Review'
        }, {
          value: 'WARNING',
          label: '🔵 Coach'
        }, {
          value: 'INFO',
          label: '⚪ Info'
        }]} variant="admin" size="sm" fullWidth={false} data-id="element-4419" data-mp="qnk8ibyl83xt" />
          </div>

          {/* Stage Filter Pills */}
          <div className="flex flex-wrap gap-2" data-id="element-4420" data-mp="3hp2mlqn0nuhy">
            <FilterIcon className="h-4 w-4 text-gray-400 self-center" data-id="element-4421" data-mp="3cn526t2vqtnf" />
            {ALL_STAGES.map(stage => <button key={stage} onClick={() => setSelectedStage(stage)} className={`rounded-full px-3 py-1 text-xs font-medium transition-all ${selectedStage === stage ? 'bg-gray-800 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`} data-id="element-4422" data-mp="mgf63ns3pgt">
                {stage === 'all' ? 'Alle fasen' : STAGE_LABELS[stage as Stage]}
              </button>)}
          </div>

          <p className="text-sm text-gray-500" data-id="element-4423" data-mp="oa7p50lujqex">
            {filtered.length} van {RULES.length} regels weergegeven
          </p>

          <div className="space-y-2" data-id="element-4424" data-mp="talyclz6p6ej">
            {filtered.length === 0 ? <div className="rounded-lg border border-gray-100 bg-gray-50 py-12 text-center" data-id="element-4425" data-mp="xbnyr64u7oi3">
                <SearchIcon className="mx-auto h-8 w-8 text-gray-300 mb-2" data-id="element-4426" data-mp="2v1fjdorxyftx" />
                <p className="text-gray-500" data-id="element-4427" data-mp="2v3a1ywfqpvuu">
                  Geen regels gevonden voor deze zoekopdracht.
                </p>
              </div> : filtered.map(rule => <RuleCard key={rule.id} rule={rule} data-id="element-4428" data-mp="2rl2trpkpojae" />)}
          </div>
        </>}

      {/* ── View: Regelboeken ── */}
      {view === 'regelboeken' && <div className="space-y-5" data-id="element-4429" data-mp="2htq3h2eka16h">
          <div className="rounded-lg border border-emerald-100 bg-emerald-50 p-4" data-id="element-4430" data-mp="2drleigz25sq6">
            <p className="text-sm text-emerald-800" data-id="element-4431" data-mp="2dpigb9nr4tad">
              <strong data-id="element-4432" data-mp="3agt01ozog1u3">Live regelboeken</strong> — per module een executable
              didactisch profiel uit{' '}
              <code className="font-mono bg-white/60 px-1 rounded" data-id="element-4433" data-mp="35jap5t23ncjg">
                src/governance/rulebooks/
              </code>
              . Wat je hier ziet is wat de pipeline daadwerkelijk afdwingt
              (notatie, voorkeur-visuals, vraagtypes, trap-distractors).
            </p>
            <div className="mt-3 flex flex-wrap items-center gap-2 text-xs" data-id="element-4434" data-mp="1q2scq3vr8qnn">
              <span className="inline-flex items-center gap-1 rounded-full bg-violet-50 text-violet-700 border border-violet-100 px-2 py-0.5 font-medium" data-id="element-4435" data-mp="1l2o6d6ok9cb5">
                <SparklesIcon className="h-3 w-3" data-id="element-4436" data-mp="3528f5dpjk545" />
                {countRoadmapEntries().futureQuestionTypes} toekomstige
                vraagtypes
              </span>
              <span className="inline-flex items-center gap-1 rounded-full bg-violet-50 text-violet-700 border border-violet-100 px-2 py-0.5 font-medium" data-id="element-4437" data-mp="3dyheksdir3qa">
                <SparklesIcon className="h-3 w-3" data-id="element-4438" data-mp="3nukr8s5zykrh" />
                {countRoadmapEntries().futureVisuals} toekomstige visuals
              </span>
              <span className="text-emerald-700/70" data-id="element-4439" data-mp="3ivhioxqt5wer">
                op de roadmap — open een regelboek om de plannen per module te
                zien.
              </span>
            </div>
          </div>

          {/* Live module rulebooks (Phase 3) */}
          <div data-id="element-4440" data-mp="22h7woaa1gjr3">
            <h3 className="text-sm font-semibold uppercase tracking-wide text-gray-500 mb-3 flex items-center gap-2" data-id="element-4441" data-mp="22isp9ucslsmb">
              <FolderIcon className="h-4 w-4" data-id="element-4442" data-mp="1yhqqtfqp9kle" />
              Module-Regelboeken ({listModuleRulebooks().length})
            </h3>
            <div className="space-y-2" data-id="element-4443" data-mp="1oelf4nakvd98">
              {listModuleRulebooks().map(rb => <LiveRulebookCard key={rb.moduleId} rulebook={rb} color={moduleColorForShortCode(rb.shortCode)} data-id="element-4444" data-mp="1sfnfj7g8dvcs" />)}
            </div>
          </div>

          {/* Pipeline source files */}
          <div data-id="element-4445" data-mp="14motfe3lakv0">
            <h3 className="text-sm font-semibold uppercase tracking-wide text-gray-500 mb-3 flex items-center gap-2" data-id="element-4446" data-mp="14qcm6b17n38o">
              <CodeIcon className="h-4 w-4" data-id="element-4447" data-mp="vnjuw7s12riu" />
              Pipeline Bronbestanden (TypeScript)
            </h3>
            <div className="rounded-xl border border-gray-100 bg-white overflow-hidden" data-id="element-4448" data-mp="10md7zz3ekgih">
              {PIPELINE_FILES.map((file, i) => <div key={file.path} className={`flex items-start gap-3 px-4 py-3 ${i < PIPELINE_FILES.length - 1 ? 'border-b border-gray-50' : ''}`} data-id="element-4449" data-mp="2q2w6hhz03wm6" data-mpi={i}>
                  <span className={`rounded-full px-2 py-0.5 text-xs font-medium flex-shrink-0 mt-0.5 ${STAGE_COLORS[file.stage]}`} data-id="element-4450" data-mp="2snkgfe02xfnu">
                    {STAGE_LABELS[file.stage]}
                  </span>
                  <div className="min-w-0 flex-1" data-id="element-4451" data-mp="2em82t98e5q54">
                    <p className="text-sm font-semibold text-gray-800" data-id="element-4452" data-mp="2ensv0s78gwzt">
                      {file.name}
                    </p>
                    <code className="text-xs text-gray-400 font-mono" data-id="element-4453" data-mp="3cbt7tltsthma">
                      {file.path}
                    </code>
                    <p className="text-xs text-gray-500 mt-0.5" data-id="element-4454" data-mp="32jneo8rt8u5i">
                      {file.description}
                    </p>
                  </div>
                </div>)}
            </div>
          </div>
        </div>}

      {/* ── View: Family-hints (P1-4) ── */}
      {view === 'family-hints' && <RulebookHintsPanel data-id="element-4455" data-mp="3q8q7mcrwa9fb" />}

      {/* ── View: Drempelwaarden ── */}
      {view === 'drempelwaarden' && <ThresholdSection data-id="element-4456" data-mp="1gl59tqw2yl7" />}
    </div>;
}